import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { isMasterEmail } from '../constants';

export interface TeamPermissions {
  can_view_margin: boolean;           // Ver margem de lucro e custo de insumos
  can_edit_final_price: boolean;       // Modificar preço calculado no orçamento
  can_delete_records: boolean;         // Excluir orçamentos, clientes, OS
  can_view_cost_center: boolean;       // Centro de custos & pró-labore
  can_view_executive_cockpit: boolean; // DRE e Cockpit Executivo de faturamento
  can_edit_pix: boolean;               // Modificar Chave PIX e dados de recebimento
  can_export_clients: boolean;         // Exportar carteira de clientes CSV/Excel
  can_view_all_work_orders: boolean;   // Ver todas as OS ou apenas atribuídas
  can_issue_receipts: boolean;         // Emitir recibos financeiros
  can_view_suppliers: boolean;         // Ver lista de fornecedores e compras
}

export const DEFAULT_PERMISSIONS_PRESETS: Record<string, TeamPermissions> = {
  vendedor: {
    can_view_margin: false, // Blindagem de custos reais
    can_edit_final_price: true, // Ativa chave de vendas (negociação de preço e desconto)
    can_delete_records: false,
    can_view_cost_center: false,
    can_view_executive_cockpit: false,
    can_edit_pix: false,
    can_export_clients: false,
    can_view_all_work_orders: true, // Acompanhamento de pedidos e orçamentos
    can_issue_receipts: true, // Emissão de recibos de vendas
    can_view_suppliers: false,
  },
  tecnico: {
    can_view_margin: false,
    can_edit_final_price: false,
    can_delete_records: false,
    can_view_cost_center: false,
    can_view_executive_cockpit: false,
    can_edit_pix: false,
    can_export_clients: false,
    can_view_all_work_orders: false, // apenas as suas
    can_issue_receipts: false,
    can_view_suppliers: false,
  },
  gerente: {
    can_view_margin: true,
    can_edit_final_price: true,
    can_delete_records: false,
    can_view_cost_center: true,
    can_view_executive_cockpit: true,
    can_edit_pix: false, // Chave PIX permanece blindada apenas para o Master!
    can_export_clients: true,
    can_view_all_work_orders: true,
    can_issue_receipts: true,
    can_view_suppliers: true,
  },
  atendente: {
    can_view_margin: false,
    can_edit_final_price: false,
    can_delete_records: false,
    can_view_cost_center: false,
    can_view_executive_cockpit: false,
    can_edit_pix: false,
    can_export_clients: false,
    can_view_all_work_orders: true,
    can_issue_receipts: true,
    can_view_suppliers: false,
  },
  contador: {
    can_view_margin: false,
    can_edit_final_price: false,
    can_delete_records: false,
    can_view_cost_center: true,
    can_view_executive_cockpit: true,
    can_edit_pix: false,
    can_export_clients: false, // LGPD compliance: sem carteira de clientes
    can_view_all_work_orders: false,
    can_issue_receipts: false,
    can_view_suppliers: true,
  },
  accountant: {
    can_view_margin: false,
    can_edit_final_price: false,
    can_delete_records: false,
    can_view_cost_center: true,
    can_view_executive_cockpit: true,
    can_edit_pix: false,
    can_export_clients: false,
    can_view_all_work_orders: false,
    can_issue_receipts: false,
    can_view_suppliers: true,
  }
};

export interface TeamMember {
  id: string;
  workspace_owner_id: string;
  member_user_id?: string | null;
  invite_email: string;
  name: string;
  role: string;
  permissions: TeamPermissions;
  status: 'pending' | 'active' | 'suspended';
  invite_token: string;
  created_at: string;
}

export interface TeamSummary {
  total_members: number;
  max_included_members: number;
  slots_remaining: number;
}

export type PermissionKey = 
  | keyof TeamPermissions
  | 'view_margin'
  | 'edit_final_price'
  | 'delete_records'
  | 'view_cost_center'
  | 'view_executive_cockpit'
  | 'edit_pix'
  | 'export_clients'
  | 'view_all_work_orders'
  | 'issue_receipts'
  | 'view_suppliers';

interface TeamPermissionsContextValue {
  isMaster: boolean;
  isMember: boolean;
  effectiveWorkspaceId: string | null;
  currentMemberProfile: TeamMember | null;
  teamMembers: TeamMember[];
  teamSummary: TeamSummary;
  isLoadingTeam: boolean;
  can: (permission: PermissionKey) => boolean;
  inviteMember: (name: string, email: string, role: string, permissions: TeamPermissions) => Promise<{ success: boolean; token?: string; error?: string }>;
  updateMemberPermissions: (memberId: string, permissions: TeamPermissions) => Promise<boolean>;
  toggleMemberStatus: (memberId: string, currentStatus: string) => Promise<boolean>;
  removeMember: (memberId: string) => Promise<boolean>;
  refreshTeam: () => Promise<void>;
}

const TeamPermissionsContext = createContext<TeamPermissionsContextValue>({
  isMaster: true,
  isMember: false,
  effectiveWorkspaceId: null,
  currentMemberProfile: null,
  teamMembers: [],
  teamSummary: { total_members: 0, max_included_members: 10, slots_remaining: 10 },
  isLoadingTeam: false,
  can: () => true,
  inviteMember: async () => ({ success: false, error: 'Não inicializado' }),
  updateMemberPermissions: async () => false,
  toggleMemberStatus: async () => false,
  removeMember: async () => false,
  refreshTeam: async () => {},
});

export const useTeamPermissions = () => useContext(TeamPermissionsContext);

export const TeamPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const userEmail = session?.user?.email?.toLowerCase().trim();

  const [isMember, setIsMember] = useState(false);
  const [currentMemberProfile, setCurrentMemberProfile] = useState<TeamMember | null>(null);
  const [effectiveWorkspaceId, setEffectiveWorkspaceId] = useState<string | null>(userId || null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [teamSummary, setTeamSummary] = useState<TeamSummary>({
    total_members: 0,
    max_included_members: 10,
    slots_remaining: 10
  });

  const loadMembershipAndTeam = useCallback(async () => {
    if (!userId) {
      setIsMember(false);
      setCurrentMemberProfile(null);
      setEffectiveWorkspaceId(null);
      setTeamMembers([]);
      return;
    }

    setIsLoadingTeam(true);

    try {
      // 1. Verificar se o usuário logado é membro colaborador de algum Master
      const { data: memberData } = await supabase
        .from('workspace_members')
        .select('*')
        .or(`member_user_id.eq.${userId},invite_email.eq.${userEmail}`)
        .eq('status', 'active')
        .maybeSingle();

      if (memberData) {
        setIsMember(true);
        setCurrentMemberProfile(memberData as TeamMember);
        setEffectiveWorkspaceId(memberData.workspace_owner_id);
        setIsLoadingTeam(false);
        return;
      }

      // 2. Se não for colaborador, o usuário é o Master/Owner do seu próprio workspace
      setIsMember(false);
      setCurrentMemberProfile(null);
      setEffectiveWorkspaceId(userId);

      // Carregar os membros gerenciados por ele (se houver)
      const { data: members, error } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_owner_id', userId)
        .order('created_at', { ascending: false });

      if (!error && members) {
        setTeamMembers(members as TeamMember[]);
        const count = members.length;
        setTeamSummary({
          total_members: count,
          max_included_members: 10,
          slots_remaining: Math.max(0, 10 - count)
        });
      }
    } catch (err) {
      console.warn('[TeamPermissions] Erro ao carregar dados de equipe:', err);
    } finally {
      setIsLoadingTeam(false);
    }
  }, [userId, userEmail]);

  useEffect(() => {
    loadMembershipAndTeam();
  }, [loadMembershipAndTeam]);

  // Função central para checagem de travas e liberdades
  const isMasterAccount = isMasterEmail(userEmail);

  const can = useCallback((permission: PermissionKey): boolean => {
    // Se for usuário Master ou proprietário do workspace, tem acesso irrestrito a todas as áreas
    if (isMasterAccount || !isMember || !currentMemberProfile) {
      return true;
    }

    const key = (permission.startsWith('can_') ? permission : `can_${permission}`) as keyof TeamPermissions;
    // Se for colaborador, checa estritamente o mapa de permissões configurado pelo Master
    return Boolean(currentMemberProfile.permissions?.[key]);
  }, [isMasterAccount, isMember, currentMemberProfile]);

  // Convidar novo colaborador (com limite de até 10)
  const inviteMember = useCallback(async (
    name: string, 
    email: string, 
    role: string, 
    permissions: TeamPermissions
  ): Promise<{ success: boolean; token?: string; error?: string }> => {
    if (!userId) return { success: false, error: 'Usuário não autenticado' };

    if (teamMembers.length >= 10) {
      return { 
        success: false, 
        error: 'Você atingiu o limite de 10 colaboradores inclusos no Plano Premium. Entre em contato para expandir para o plano Corporativo.' 
      };
    }

    const cleanEmail = email.toLowerCase().trim();

    try {
      const inviteToken = `${Date.now()}_${Math.random().toString(36).substring(2, 12)}`;

      const { data, error } = await supabase
        .from('workspace_members')
        .insert({
          workspace_owner_id: userId,
          name: name.trim(),
          invite_email: cleanEmail,
          role: role || 'vendedor',
          permissions,
          status: 'pending',
          invite_token: inviteToken
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'Este e-mail já foi convidado para a sua equipe.' };
        }
        return { success: false, error: error.message };
      }

      await loadMembershipAndTeam();
      return { success: true, token: data.invite_token };
    } catch (err: any) {
      return { success: false, error: err.message || 'Falha ao gerar convite.' };
    }
  }, [userId, teamMembers.length, loadMembershipAndTeam]);

  // Atualizar permissões de um membro existente
  const updateMemberPermissions = useCallback(async (memberId: string, permissions: TeamPermissions): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ permissions, updated_at: new Date().toISOString() })
        .eq('id', memberId)
        .eq('workspace_owner_id', userId);

      if (!error) {
        setTeamMembers(prev => prev.map(m => m.id === memberId ? { ...m, permissions } : m));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Erro ao atualizar permissões:', err);
      return false;
    }
  }, [userId]);

  // Alternar status (Pausar / Reativar acesso)
  const toggleMemberStatus = useCallback(async (memberId: string, currentStatus: string): Promise<boolean> => {
    const nextStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ status: nextStatus, updated_at: new Date().toISOString() })
        .eq('id', memberId)
        .eq('workspace_owner_id', userId);

      if (!error) {
        setTeamMembers(prev => prev.map(m => m.id === memberId ? { ...m, status: nextStatus as any } : m));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      return false;
    }
  }, [userId]);

  // Remover membro da equipe
  const removeMember = useCallback(async (memberId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .delete()
        .eq('id', memberId)
        .eq('workspace_owner_id', userId);

      if (!error) {
        setTeamMembers(prev => prev.filter(m => m.id !== memberId));
        setTeamSummary(prev => ({
          ...prev,
          total_members: Math.max(0, prev.total_members - 1),
          slots_remaining: Math.min(10, prev.slots_remaining + 1)
        }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Erro ao remover membro:', err);
      return false;
    }
  }, [userId]);

  return (
    <TeamPermissionsContext.Provider
      value={{
        isMaster: isMasterAccount || !isMember,
        isMember,
        effectiveWorkspaceId,
        currentMemberProfile,
        teamMembers,
        teamSummary,
        isLoadingTeam,
        can,
        inviteMember,
        updateMemberPermissions,
        toggleMemberStatus,
        removeMember,
        refreshTeam: loadMembershipAndTeam
      }}
    >
      {children}
    </TeamPermissionsContext.Provider>
  );
};
